import { useSelector } from 'react-redux'
import Immutable from 'immutable'

/**
 * Gets the initial form data for the theme settings form. The initial form
 * data comprises all available settings. Default values are merged with the
 * values of current settings.
 *
 * @private
 * @param {Immutable.List<Immutable.Map>} settingsSchema
 *   Theme settings schema (via the GetThemeSettingsSchema Unity API).
 * @param {Immutable.Map} currentSettings
 *   Current theme settings (via the GetThemeSettings Unity API).
 * @param {Immutable.Map} defaultSettings
 *   Current theme default settings (via the GetDefaultThemeSettings Unity API).
 * @returns {Immutable.Map} Initial form data
 */
export function getInitialFormData(
  settingsSchema: Immutable.List<Immutable.Map<string, any>>,
  currentSettings: Immutable.Map<string, any>,
  defaultSettings: Immutable.Map<string, any>,
): Immutable.Map<string, any> {
  // Recursively collect default values for all available setting definitions
  const defaultValues = Immutable.fromJS(
    settingsSchema.reduce(collectThemeSettingDefaultValues, {} as Immutable.Map<string, any>),
  )

  return defaultValues.mergeDeep(defaultSettings, currentSettings)
}

function collectThemeSettingDefaultValues(
  items: Immutable.Map<string, Immutable.Map<string, any>>,
  item: Immutable.Map<string, any>,
): Immutable.Map<string, Immutable.Map<string, any>> {
  items[item.get('name')] = (function () {
    switch (item.get('type')) {
      case 'input':
      case 'customCss':
      case 'radioButtons':
        return ''
      case 'rangeWithInput':
        return 50
      case 'color':
        return 'white'
      case 'font':
        return 'sans-serif'
      case 'checkbox':
        return false
      case 'group':
        return item.get('settings').reduce(collectThemeSettingDefaultValues, {})
      case 'selection':
        return item.get('options').reduce((options, option) => {
          return collectThemeSettingDefaultValues({ ...options, [`${option.get('name')}Selected`]: false }, option)
        }, {})
      default:
        return null
    }
  })()

  return items
}

function useThemeSettings(omitSettings: Array<string> = []): Immutable.Map<string, any> {
  const currentSettings = useSelector<State, { [key: string]: any }>((state) => state.getIn(['shop', 'themeSettings']))
  const themeSettingsSchema = useSelector<State, Immutable.List<Immutable.Map<string, any>>>((state) =>
    state
      .get('themeSettingsSchema')
      .filter((themeSettingSchema) => !omitSettings.includes(themeSettingSchema.get('name'))),
  )
  const defaultThemeSettings = useSelector<State, State>((state) => state.get('defaultThemeSettings'))
  const themeSettings = getInitialFormData(themeSettingsSchema, Immutable.fromJS(currentSettings), defaultThemeSettings)

  return themeSettings
}

export default useThemeSettings
